import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import CookieBanner from "@/components/site/CookieBanner";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Check, Info } from "lucide-react";
import DemoDialog from "@/components/site/DemoDialog";
import { track, REGISTER_URL } from "@/lib/analytics";
import brokerImg from "@/assets/broker-hero.jpg";
import supplierImg from "@/assets/supplier-shop.jpg";
import { useSeo, ORG_SCHEMA, buildFaqSchema, buildBreadcrumbSchema, buildServiceSchema } from "@/lib/seo";

type Role = "broker" | "supplier";

const imgMap = { broker: brokerImg, supplier: supplierImg };
const toneMap: Record<string, string> = { mint: "bg-mint", coral: "bg-coral", butter: "bg-butter", lavender: "bg-lavender" };

const seoFor = (role: Role, lang: "nl" | "en") => {
  if (role === "broker") {
    return lang === "nl"
      ? {
          title: "Marketplace voor makelaars in eigen merk | Tribeo",
          description: "Bouw je eigen marketplace in jouw merk, met de leveranciers die jij vertrouwt. Verdien mee op fotografie, onderhoud, verbouwen en verduurzaming. Gratis te starten.",
          ogTitle: "Jouw eigen marketplace, jouw merk, jouw leveranciers",
          ogDescription: "Verdien per bestelling mee op fotografie, styling, onderhoud en verduurzaming. Eigen merk, eigen domein, gratis te starten.",
          keywords: "marketplace voor makelaars, software voor makelaars, eigen platform makelaar, extra omzet makelaarskantoor, woningdiensten makelaar, makelaar innovatie",
        }
      : {
          title: "Marketplace for real estate agents | Your branded platform | Tribeo",
          description: "Build your own marketplace in your brand, with the vendors you trust. Earn on every home service your clients order. Free to start, no subscription.",
          ogTitle: "Your branded marketplace, your vendors, your margins",
          ogDescription: "Earn on photography, styling, maintenance and sustainability orders. Your brand, your domain, free to start.",
          keywords: "marketplace for real estate agents, real estate agent software, branded platform agents, property services agents, agent extra revenue",
        };
  }
  return lang === "nl"
    ? {
        title: "Klanten via makelaars in jouw regio | Gratis webshop | Tribeo",
        description: "Verschijn in de marketplace van makelaars in jouw werkgebied en krijg er een gratis eigen webshop bij. Geen abonnement. Je betaalt 6% bij een geslaagde verkoop.",
        ogTitle: "Warme klanten via makelaars en een gratis eigen webshop",
        ogDescription: "Eén catalogus, meerdere marketplaces van makelaars. Gratis eigen webshop. Betalen pas bij verkoop.",
        keywords: "klanten via makelaars, gratis webshop hovenier, gratis webshop schilder, marketplace woningdiensten, leads voor leveranciers, hovenier makelaar, woningonderhoud platform",
      }
    : {
        title: "Reach homeowners through real estate agents | Free webshop | Tribeo",
        description: "Appear in agent marketplaces in your area when clients need your services, and get a free webshop on top. No subscription. Pay 6% only on a successful sale.",
        ogTitle: "Warm clients through agents and a free webshop",
        ogDescription: "One catalogue, multiple agent marketplaces. Free webshop. Pay only when you sell.",
        keywords: "clients through real estate agents, free webshop home services, property services marketplace, leads for vendors, landscaper marketplace, home maintenance platform",
      };
};

const pathsFor = (role: Role) =>
  role === "broker"
    ? { nlPath: "/voor-makelaars", enPath: "/en/for-agents" }
    : { nlPath: "/voor-leveranciers", enPath: "/en/for-vendors" };

const Content = ({ role }: { role: Role }) => {
  const { t, lang } = useLang();
  const s = t.solutions[role];
  const meta = seoFor(role, lang);
  const paths = pathsFor(role);
  const url = `https://tribeo.co${lang === "nl" ? paths.nlPath : paths.enPath}`;
  const audience =
    role === "broker"
      ? lang === "nl" ? "Makelaars en makelaarskantoren" : "Real estate agents"
      : lang === "nl" ? "Leveranciers van woningdiensten" : "Home service vendors";
  useSeo(lang, {
    ...meta,
    ...paths,
    jsonLd: [
      ORG_SCHEMA,
      buildFaqSchema(s.faq.items),
      buildServiceSchema({ name: meta.title, description: meta.description, url, audience }),
      buildBreadcrumbSchema([
        { name: "Home", path: lang === "nl" ? "/" : "/en" },
        { name: role === "broker" ? t.solutionsNav.broker : t.solutionsNav.supplier, path: lang === "nl" ? paths.nlPath : paths.enPath },
      ]),
    ],
  });

  return (
    <>
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-20 md:pb-28 bg-cream">
        <div className="container grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-glow">{s.hero.eyebrow}</span>
            <h1 className="mt-3 text-4xl md:text-6xl font-extrabold leading-[1.05]">{s.hero.title}</h1>
            <p className="mt-6 text-lg md:text-xl text-muted-foreground">{s.hero.lead}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("register_click", `solution_${role}`)}>
                <Button size="lg" className="rounded-full h-12 px-7 text-base font-bold w-full sm:w-auto">
                  {s.hero.cta1} <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </a>
              <DemoDialog
                trigger={
                  <Button size="lg" variant="outline" className="rounded-full h-12 px-7 text-base font-bold">
                    {s.hero.cta2}
                  </Button>
                }
              />
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-0 -m-6 rounded-[2.5rem] bg-lavender/40 blur-2xl" />
            <img
              src={imgMap[s.hero.image]}
              alt={s.hero.title}
              width={1280}
              height={896}
              className="relative rounded-[2rem] shadow-elegant w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container max-w-4xl">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-3xl bg-secondary p-8">
              <div className="text-xs font-bold uppercase tracking-widest text-primary-glow">Wat is het</div>
              <p className="mt-3 text-lg leading-relaxed">{s.intro.what}</p>
            </div>
            <div className="rounded-3xl bg-secondary p-8">
              <div className="text-xs font-bold uppercase tracking-widest text-primary-glow">Waarom</div>
              <p className="mt-3 text-lg leading-relaxed">{s.intro.why}</p>
            </div>
          </div>
          {s.notReplaces && (
            <div className="mt-8 rounded-2xl border border-border bg-background p-5 flex items-start gap-3">
              <Info className="h-5 w-5 text-primary-glow shrink-0 mt-0.5" />
              <p className="text-sm md:text-base text-foreground/80">{s.notReplaces}</p>
            </div>
          )}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 bg-soft">
        <div className="container">
          <h2 className="text-3xl md:text-5xl font-extrabold leading-[1.05] max-w-2xl">{s.benefits.title}</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {s.benefits.items.map((it) => (
              <div key={it.t} className="rounded-2xl bg-background p-6 shadow-card">
                <div className={`h-10 w-10 rounded-xl ${toneMap[it.tone]} flex items-center justify-center`}>
                  <Check className="h-5 w-5" />
                </div>
                <div className="mt-4 text-lg font-extrabold leading-tight">{it.t}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{it.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep dive (Mews-style) */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-glow">{s.deepDive.eyebrow}</span>
            <h2 className="mt-3 text-3xl md:text-5xl font-extrabold leading-[1.05]">{s.deepDive.title}</h2>
            <p className="mt-5 text-lg md:text-xl text-muted-foreground">{s.deepDive.lead}</p>
          </div>
          <div className="mt-14 space-y-5">
            {s.deepDive.sections.map((sec, i) => (
              <div key={sec.title} className="grid lg:grid-cols-12 gap-8 rounded-3xl bg-cream p-8 md:p-10">
                <div className="lg:col-span-1 text-2xl font-extrabold text-primary-glow">{String(i + 1).padStart(2, "0")}</div>
                <div className="lg:col-span-5">
                  <h3 className="text-2xl md:text-3xl font-extrabold leading-tight">{sec.title}</h3>
                  <p className="mt-4 text-base md:text-lg text-foreground/80 leading-relaxed">{sec.body}</p>
                </div>
                <div className="lg:col-span-6">
                  {sec.bullets && (
                    <ul className="space-y-3">
                      {sec.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-3 rounded-2xl bg-background p-4 shadow-card">
                          <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground shrink-0">
                            <Check className="h-3.5 w-3.5" />
                          </span>
                          <span className="text-sm md:text-base">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 md:py-28 bg-soft">
        <div className="container">
          <h2 className="text-3xl md:text-5xl font-extrabold leading-[1.05] max-w-2xl">{s.steps.title}</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {s.steps.items.map((st) => (
              <div key={st.num} className="rounded-3xl bg-background p-8 shadow-card">
                <div className="text-sm font-bold text-primary-glow">{st.num}</div>
                <div className="mt-3 text-2xl font-extrabold leading-tight">{st.t}</div>
                <p className="mt-3 text-base text-muted-foreground">{st.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-background border-t border-border">
        <div className="container max-w-3xl">
          <h2 className="text-3xl md:text-5xl font-extrabold leading-tight text-center">{s.faq.title}</h2>
          <Accordion type="single" collapsible className="mt-10">
            {s.faq.items.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-border">
                <AccordionTrigger className="text-left text-base font-bold hover:no-underline">{item.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container max-w-3xl text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold leading-tight">{t.cta.title}</h2>
          <p className="mt-4 text-lg text-muted-foreground">{t.cta.desc}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("register_click", `solution_${role}_bottom`)}>
              <Button size="lg" className="rounded-full h-12 px-7 text-base font-bold w-full sm:w-auto">
                {t.cta.button} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </a>
            <DemoDialog
              trigger={
                <Button size="lg" variant="outline" className="rounded-full h-12 px-7 text-base font-bold">
                  {t.cta.secondary}
                </Button>
              }
            />
          </div>
        </div>
      </section>
    </>
  );
};

const SolutionPage = ({ role }: { role: Role }) => (
  <LanguageProvider>
    <div className="min-h-screen bg-background">
      <Header />
      <main><Content role={role} /></main>
      <Footer />
      <CookieBanner />
    </div>
  </LanguageProvider>
);

export default SolutionPage;
